/**
 * Auth Service - QR + SMS Login
 *
 * Handles:
 * - QR session creation
 * - Polling for auth status
 * - Token storage and refresh
 */

import { AUTH_BASE, API_BASE } from './api'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getStoredUser as getStoredUserFromStore,
  hasValidToken,
  storeTokens as storeSessionTokens,
  storeUser,
} from './tokenStore'

// ============================================
// Types
// ============================================

export interface QrSessionResponse {
  session_id: string
  short_code: string
  qr_data: string      // "SMSTO:+13179833826:7X4K"
  sms_number: string   // "+13179833826"
  expires_in: number   // Seconds
}

export interface AuthUser {
  id: string
  phone: string | null
  email: string | null
  display_name: string | null
  is_new: boolean
}

export interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

export interface SessionStatusResponse {
  status: 'pending' | 'authenticated' | 'expired'
  user: AuthUser | null
  tokens: TokenPair | null
  is_new: boolean
}

export interface AuthCallbackParams {
  code: string | null
  token: string | null
  refreshToken: string | null
  userId: string
  email: string
  name: string
  isNew: boolean
  error: string | null
}

export interface AuthExchangeResponse {
  access_token: string
  refresh_token: string
  user_id: string
  email: string
  name: string
  is_new: boolean
}

const AUTH_CALLBACK_QUERY_KEYS = [
  'code',
  'token',
  'refresh_token',
  'user_id',
  'email',
  'name',
  'is_new',
  'error',
] as const

// ============================================
// API Functions
// ============================================

/**
 * Create a new QR login session
 */
export async function createQrSession(): Promise<QrSessionResponse> {
  const response = await fetch(`${AUTH_BASE}/api/auth/qr-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to create QR session: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Send an email magic link to sign in.
 *
 * Always returns { success: true } from the server to prevent email enumeration.
 */
export async function sendMagicLink(
  email: string,
  redirectUri: string
): Promise<{ success: boolean; request_id?: string }> {
  const response = await fetch(`${AUTH_BASE}/api/auth/email/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, redirect_uri: redirectUri }),
  })

  // Even if non-200, treat as success for UX symmetry with backend anti-enumeration.
  if (!response.ok) return { success: true }
  try {
    return await response.json()
  } catch {
    return { success: true }
  }
}

/**
 * Poll for magic link completion (cross-device support).
 * Returns token data when the user clicks the link on any device.
 */
export async function pollMagicLink(
  requestId: string
): Promise<{ status: 'pending' | 'completed'; token?: string; refresh_token?: string; user_id?: string; email?: string; name?: string; is_new?: boolean }> {
  const response = await fetch(`${AUTH_BASE}/api/auth/email/poll/${requestId}`)
  if (!response.ok) return { status: 'pending' }
  try {
    return await response.json()
  } catch {
    return { status: 'pending' }
  }
}

/**
 * Check QR session status (poll for auth completion)
 */
export async function checkQrSession(sessionId: string): Promise<SessionStatusResponse> {
  const response = await fetch(`${AUTH_BASE}/api/auth/qr-session/${sessionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to check session: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Refresh access token using refresh token
 */
export async function refreshToken(refreshToken: string): Promise<TokenPair> {
  const response = await fetch(`${AUTH_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    throw new Error(`Failed to refresh token: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Exchange one-time OAuth callback code for session tokens.
 */
export async function exchangeAuthCode(code: string): Promise<AuthExchangeResponse> {
  const response = await fetch(`${AUTH_BASE}/api/auth/exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Failed to exchange code' }))
    throw new Error(data.error || 'Failed to exchange code')
  }

  return response.json()
}

/**
 * Parse OAuth callback fields from the current URL search string.
 */
export function getAuthCallbackParams(search?: string): AuthCallbackParams {
  const source = search ?? (typeof window !== 'undefined' ? window.location.search : '')
  const params = new URLSearchParams(source)
  return {
    code: params.get('code'),
    token: params.get('token'),
    refreshToken: params.get('refresh_token'),
    userId: params.get('user_id') || '',
    email: params.get('email') || '',
    name: params.get('name') || '',
    isNew: params.get('is_new') === 'true',
    error: params.get('error'),
  }
}

/**
 * Remove OAuth callback query params from the current URL while preserving other params.
 */
export function clearAuthCallbackParamsFromCurrentUrl(): void {
  if (typeof window === 'undefined') return
  const next = new URL(window.location.href)
  for (const key of AUTH_CALLBACK_QUERY_KEYS) {
    next.searchParams.delete(key)
  }
  const relative = `${next.pathname}${next.search}${next.hash}`
  window.history.replaceState({}, '', relative)
}

/**
 * Logout - invalidate current session
 */
export async function logout(): Promise<void> {
  const accessToken = getAccessToken()

  if (accessToken) {
    try {
      await fetch(`${AUTH_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      })
    } catch (e) {
      // Ignore errors on logout
    }
  }

  clearTokens()
}

// ============================================
// Token Management
// ============================================

/**
 * Store auth result after successful auth.
 */
export function storeAuthResult(tokens: TokenPair, user: AuthUser): void {
  storeSessionTokens(tokens.access_token, tokens.refresh_token)
  storeUser(user)
}

export { clearTokens, getAccessToken, getRefreshToken }
export function getStoredUser<T = AuthUser>(): T | null {
  return getStoredUserFromStore<T>()
}

/**
 * Check if user is logged in (has a valid token under either key)
 */
export function isLoggedIn(): boolean {
  return hasValidToken()
}

/**
 * Ensure `loveline_user` is populated for authenticated Loveline users.
 */
export async function ensureStoredUser(): Promise<AuthUser | null> {
  const existing = getStoredUser<AuthUser>()
  if (existing) return existing

  const token = getAccessToken()
  if (!token) return null

  try {
    const res = await fetch(`${API_BASE}/api/user/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
    if (!res.ok) return null
    const data = await res.json() as any
    const profile = data?.profile ?? {}

    const user: AuthUser = {
      id: String(profile.id ?? ''),
      phone: profile.phone ?? null,
      email: profile.email ?? null,
      display_name: profile.display_name ?? null,
      is_new: false,
    }

    if (!user.id) return null
    storeUser(user)
    return user
  } catch {
    return null
  }
}

// ============================================
// QR Code Helper
// ============================================

/**
 * Generate QR code data URL using a simple canvas approach
 * For production, use a proper QR library like 'qrcode'
 */
export function formatPhoneNumber(phone: string): string {
  // Format: +1 (317) 983-3826
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return phone
}
